import type { User } from '@workos-inc/authkit-js';
import { AuthKitProvider, useAuth as useWorkOSAuth } from '@workos-inc/authkit-react';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { STORAGE_KEYS } from '@ophelos/shared/constants/storage-keys';
import { getConnectApiBaseUrl } from '@/lib/connect-api';

/**
 * Decode a JWT payload without verifying the signature.
 * Returns null if the token is malformed.
 */
function decodeJwtPayload(token: string): { exp?: number } | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    // JWT payloads are base64url-encoded (RFC 7515): convert to standard base64 for atob
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));
    return payload;
  } catch {
    return null;
  }
}

/** Buffer in seconds — refresh before the token actually expires. */
const TOKEN_REFRESH_BUFFER = 30;

/** In-flight refresh promise to avoid concurrent refresh calls. */
let refreshPromise: Promise<string | null> | null = null;

/**
 * Call the backend /auth/actions/refresh endpoint to exchange the httpOnly
 * refresh token cookie for a fresh access token.
 */
async function refreshAccessToken(): Promise<string | null> {
  try {
    const response = await fetch(`${getConnectApiBaseUrl()}/connect/v1/auth/actions/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    if (data.success && data.access_token) {
      localStorage.setItem(STORAGE_KEYS.WORKOS_JWT, data.access_token);
      return data.access_token;
    }
    return null;
  } catch (err) {
    console.error('[refreshAccessToken] fetch error:', err);
    return null;
  }
}

/**
 * Helper to safely read local auth state from localStorage
 */
function getLocalAuthState(): { authStage: string; accountId: string } | null {
  if (typeof window === 'undefined') return null;

  try {
    const authStage = localStorage.getItem(STORAGE_KEYS.AUTH_STAGE);
    const accountId = localStorage.getItem(STORAGE_KEYS.ACCOUNT_ID);
    const accessCode = localStorage.getItem(STORAGE_KEYS.ACCESS_CODE);

    return authStage && accountId && accessCode ? { authStage, accountId } : null;
  } catch {
    // localStorage can throw in private browsing mode
    return null;
  }
}

/**
 * WorkOS AuthKit Provider for Customer Portal
 *
 * Wraps the WorkOS AuthKitProvider with customer portal configuration.
 *
 * Usage in app entry point:
 * ```tsx
 * import { AuthProvider } from '@/contexts/customer-auth-context';
 *
 * <AuthProvider clientId={env.VITE_WORKOS_CLIENT_ID}>
 *   <App />
 * </AuthProvider>
 * ```
 */
interface AuthProviderProps {
  children: ReactNode;
  clientId: string;
  redirectUri?: string;
  devMode?: boolean;
  /** Custom domain for WorkOS AuthKit API (production only, e.g., "auth.next.intrum.com") */
  apiHostname?: string;
  onRedirectCallback?: () => void;
}

export function AuthProvider({
  children,
  clientId,
  redirectUri,
  devMode = false,
  apiHostname,
  onRedirectCallback,
}: AuthProviderProps) {
  return (
    <AuthKitProvider
      clientId={clientId}
      redirectUri={redirectUri}
      devMode={devMode}
      apiHostname={apiHostname}
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </AuthKitProvider>
  );
}

/**
 * Hook to access customer portal authentication state
 *
 * Two-Stage Authentication:
 * - Limited: Access code only (no 2FA) → read-only access, cannot make payments
 * - Full: Access code + 2FA completed → full access including payments
 *
 * Returns:
 * - user: Authenticated user object (WorkOS - full access only)
 * - accountId: Account ID (available in both stages)
 * - authStage: 'limited' | 'full' | null
 * - isLoading: Boolean indicating if auth state is loading
 * - isAuthenticated: Boolean indicating if user is authenticated (either stage)
 * - organizationId: Current organization ID (WorkOS org ID - full access only)
 * - signIn: Function to initiate sign-in flow
 * - signOut: Function to sign out current user (clears both WorkOS and localStorage)
 * - getAccessToken: Function to get current access token (JWT - full access only)
 *
 * @example
 * ```tsx
 * const { isAuthenticated, authStage } = useAuth();
 *
 * if (isLoading) return <Loading />;
 * if (!isAuthenticated) return <Navigate to="/login" />;
 * ```
 */
export interface UseAuthReturn {
  user: User | null;
  accountId: string | null;
  authStage: 'limited' | 'full' | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  organizationId: string | null;
  signIn: () => void;
  signOut: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  impersonator: ReturnType<typeof useWorkOSAuth>['impersonator'];
}

export function useAuth(): UseAuthReturn {
  const auth = useWorkOSAuth();

  // Track localStorage auth state (Stage 1: Access code authentication)
  const [localAuthState, setLocalAuthState] = useState(getLocalAuthState);

  // Listen for localStorage changes (from Login page or other tabs)
  useEffect(() => {
    const handleStorageChange = () => {
      setLocalAuthState(getLocalAuthState());
    };

    // Listen for cross-tab storage events (standard browser event)
    window.addEventListener('storage', handleStorageChange);
    
    // Listen for same-tab localStorage updates (custom event dispatched from Login page)
    window.addEventListener('localStorageAuthUpdate', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageAuthUpdate', handleStorageChange);
    };
  }, []);

  // Determine authentication state
  // Only two states: 'limited' (access code only) or 'full' (access code + 2FA)
  const hasWorkOSAuth = !!auth.user;
  const hasLocalAuth = !!localAuthState;
  const storedAuthStage = localAuthState?.authStage;

  // Normalize legacy auth stages: 'guest' → 'limited', 'verified' → 'full'
  const normalizedLocalStage: 'limited' | 'full' | null =
    storedAuthStage === 'full' || storedAuthStage === 'verified' ? 'full' :
    storedAuthStage === 'limited' || storedAuthStage === 'guest' ? 'limited' :
    null;

  const authStage: 'limited' | 'full' | null = hasWorkOSAuth
    ? 'full'
    : normalizedLocalStage;
  const isAuthenticated = hasWorkOSAuth || hasLocalAuth;
  const accountId = localAuthState?.accountId || null;

  // Enhanced signOut that clears both WorkOS and localStorage
  const enhancedSignOut = useCallback(async () => {
    // Clear httpOnly refresh token cookie via backend
    try {
      await fetch(`${getConnectApiBaseUrl()}/connect/v1/auth/actions/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Best-effort — cookie will expire naturally
    }

    // Clear localStorage auth state (with SSR guard and error handling)
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem(STORAGE_KEYS.AUTH_STAGE);
        localStorage.removeItem(STORAGE_KEYS.ACCOUNT_ID);
        localStorage.removeItem(STORAGE_KEYS.ACCESS_CODE);
        localStorage.removeItem(STORAGE_KEYS.CUSTOMER_NAME);
        localStorage.removeItem(STORAGE_KEYS.WORKOS_JWT);
        localStorage.removeItem(STORAGE_KEYS.USER_ID);
        localStorage.removeItem(STORAGE_KEYS.USER_EMAIL);
        // Clear fake 2FA data
        localStorage.removeItem(STORAGE_KEYS.FAKE_2FA_CODE);
        localStorage.removeItem(STORAGE_KEYS.FAKE_2FA_CODE_EXPIRY);
        localStorage.removeItem(STORAGE_KEYS.FAKE_2FA_METHOD);
        localStorage.removeItem(STORAGE_KEYS.MAGIC_AUTH_EMAIL);
        localStorage.removeItem(STORAGE_KEYS.MAGIC_AUTH_PHONE);
        localStorage.removeItem(STORAGE_KEYS.VERIFICATION_METHOD);
        // Clear persisted auth mode
        localStorage.removeItem(STORAGE_KEYS.AUTH_MODE);
      } catch {
        // Ignore localStorage errors (e.g., in private browsing mode)
      }
      // Every useAuth caller holds its own copy, and without a WorkOS session nothing reloads,
      // so tell them all: the chat above the router would otherwise keep the last customer's.
      window.dispatchEvent(new Event('localStorageAuthUpdate'));
    }
    setLocalAuthState(null);

    // Sign out from WorkOS if authenticated
    if (hasWorkOSAuth) {
      await auth.signOut();
    }
  }, [hasWorkOSAuth, auth]);

  // Get access token — reads from localStorage, auto-refreshes when expired
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    const stage = localStorage.getItem(STORAGE_KEYS.AUTH_STAGE);
    if (stage !== 'full') return null;

    const jwt = localStorage.getItem(STORAGE_KEYS.WORKOS_JWT);
    if (jwt) {
      const payload = decodeJwtPayload(jwt);
      const now = Math.floor(Date.now() / 1000);
      if (payload?.exp && payload.exp > now + TOKEN_REFRESH_BUFFER) {
        return jwt;
      }
    }

    // Token missing or expired — refresh using httpOnly cookie
    // Deduplicate concurrent refresh calls
    if (!refreshPromise) {
      refreshPromise = refreshAccessToken().finally(() => {
        refreshPromise = null;
      });
    }
    const freshToken = await refreshPromise;

    // If refresh failed, clear auth state (session expired)
    if (!freshToken) {
      await enhancedSignOut();
    }
    return freshToken;
  }, [enhancedSignOut]);

  return {
    // User data
    user: auth.user,
    accountId,
    authStage,
    isLoading: auth.isLoading,
    isAuthenticated,

    // Organization (maps to our operators)
    organizationId: auth.organizationId,

    // Auth methods
    signIn: auth.signIn,
    signOut: enhancedSignOut,

    // Token access — reads JWT from localStorage, auto-refreshes via httpOnly cookie
    getAccessToken,

    // Impersonation info (if impersonating another user)
    impersonator: auth.impersonator,
  };
}
